import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { useQueryClient } from 'react-query';
import { useHelpWizard } from './HelpWizardProvider';
import { FLOW_ROOT, getFlowNode } from './flows/helpFlows';
import HelpInlineWidget from './widgets/HelpInlineWidget';
import { dashboardAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import MatchChatModal from '../../components/MatchChatModal';
import { publicAssetUrl } from '../../utils/publicAssetUrl';
import { syncPlayerNotificationsAfterMatchChat } from '../../utils/playerNotificationQueries';

const BRAND = { blue: '#043663', red: '#D52B1E', gray: '#64748b', light: '#f1f5f9' };

const Page = styled.div`
  width: 100%;
  max-width: 820px;
`;

const PageTitle = styled.h1`
  margin: 0 0 0.35rem;
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  letter-spacing: -0.02em;
`;

const PageLead = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.98rem;
  line-height: 1.55;
  color: #475569;
`;

const Hint = styled.p`
  margin: 0 0 1.25rem;
  padding: 0.55rem 0.75rem;
  border-radius: 10px;
  background: rgba(4, 54, 99, 0.06);
  border-left: 3px solid ${BRAND.blue};
  font-size: 0.88rem;
  line-height: 1.45;
  color: #334155;
  font-weight: 500;
`;

const StepTitle = styled.h2`
  margin: 0 0 0.35rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${BRAND.blue};
  letter-spacing: -0.02em;
`;

const StepLead = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #64748b;
`;

const ChoiceBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  padding: 0.95rem 1.1rem;
  margin-bottom: 0.55rem;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
  font-size: 0.98rem;
  font-weight: 500;
  color: #0f172a;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
  &:hover {
    border-color: rgba(4, 54, 99, 0.28);
    box-shadow: 0 6px 20px rgba(4, 54, 99, 0.08);
    transform: translateY(-1px);
  }
`;

const ChoiceEmoji = styled.span`
  font-size: 1.35rem;
  line-height: 1;
  flex-shrink: 0;
`;

const Stub = styled.p`
  margin: 0 0 1rem;
  padding: 0.75rem 0.85rem;
  border-radius: 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  font-size: 0.86rem;
  line-height: 1.45;
  color: #92400e;
`;

const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0.65rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  color: ${BRAND.blue};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  margin-right: 0.5rem;
  margin-bottom: 0.45rem;
  &:hover {
    background: ${BRAND.light};
  }
`;

const TertiaryLink = styled(Link)`
  display: inline-block;
  margin-top: 0.75rem;
  font-size: 0.85rem;
  color: #64748b;
  text-decoration: underline;
  &:hover {
    color: ${BRAND.blue};
  }
`;

const TopToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 1rem;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 1.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e2e8f0;
`;

const Muted = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.9rem;
  color: ${BRAND.gray};
`;

const MatchPickCard = styled.button`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  width: 100%;
  text-align: left;
  padding: 0.85rem 1rem;
  margin-bottom: 0.55rem;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
  &:hover {
    border-color: rgba(4, 54, 99, 0.28);
    box-shadow: 0 6px 20px rgba(4, 54, 99, 0.08);
    transform: translateY(-1px);
  }
  &[data-active='true'] {
    border-color: ${BRAND.blue};
    background: rgba(4, 54, 99, 0.06);
  }
`;

const MatchLogo = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  object-fit: cover;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

const MatchLogoPh = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 800;
  flex-shrink: 0;
  border: 1px solid #e5e7eb;
`;

const MatchPickBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const MatchOpponentLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  flex-wrap: wrap;
  font-size: 0.98rem;
  font-weight: 600;
  color: #0f172a;
`;

const MatchVs = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const MatchMeta = styled.div`
  margin-top: 0.2rem;
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.4;
`;

const MatchChatHint = styled.span`
  flex-shrink: 0;
  font-size: 1.15rem;
  opacity: 0.55;
`;

const RescheduleInfoBanner = styled.div`
  margin-bottom: 1.25rem;
  padding: 1rem 1.15rem;
  border-radius: 14px;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 1px solid #fde68a;
`;

const RescheduleInfoTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1rem;
  font-weight: 700;
  color: #92400e;
`;

const RescheduleInfoBody = styled.p`
  margin: 0 0 0.65rem;
  font-size: 0.9rem;
  line-height: 1.55;
  color: #78350f;
`;

const RescheduleInfoList = styled.ul`
  margin: 0 0 1rem;
  padding-left: 1.15rem;
  font-size: 0.88rem;
  line-height: 1.5;
  color: #78350f;
  li {
    margin-bottom: 0.35rem;
  }
`;

const RescheduleAckBtn = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0.65rem 1.15rem;
  border: none;
  border-radius: 10px;
  background: ${BRAND.blue};
  color: #fff;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    filter: brightness(1.05);
  }
`;

const RescheduleReminder = styled.p`
  margin: 0 0 1rem;
  padding: 0.6rem 0.8rem;
  border-radius: 10px;
  background: rgba(4, 54, 99, 0.06);
  border-left: 3px solid ${BRAND.blue};
  font-size: 0.84rem;
  line-height: 1.45;
  color: #475569;
`;

const WidgetSection = styled.div`
  margin-top: 0.5rem;
`;

function useWizardMatches(teamCtx, { openOnly = false } = {}) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!teamCtx?.seasonId || !teamCtx?.teamId) {
      setMatches([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    const headers = teamCtx.tenantId ? { headers: { 'X-Tenant-ID': teamCtx.tenantId } } : {};
    dashboardAPI
      .getOverview(teamCtx.seasonId, teamCtx.teamId, headers)
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.recentMatches || [];
        const isOpen = (m) =>
          m &&
          m.id &&
          m.status !== 'completed' &&
          m.status !== 'cancelled' &&
          m.status !== 'canceled';
        const pending = list.filter(isOpen);
        if (openOnly) {
          setMatches(pending);
        } else {
          setMatches(pending.length > 0 ? pending : list);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [teamCtx?.teamId, teamCtx?.seasonId, teamCtx?.tenantId, openOnly]);

  return { matches, loading, error };
}

function MatchOpponentLogo({ match }) {
  const src = match?.opponentTeamLogoUrl ? publicAssetUrl(match.opponentTeamLogoUrl) : null;
  if (src) {
    return <MatchLogo src={src} alt="" />;
  }
  const initial = (match?.opponent || '?').trim().slice(0, 1).toUpperCase();
  return <MatchLogoPh aria-hidden>{initial}</MatchLogoPh>;
}

function matchPickerMeta(match, t) {
  const parts = [];
  if (match?.dateDisplay) parts.push(match.dateDisplay);
  if (match?.matchDateTime) parts.push(match.matchDateTime);
  if (match?.courtDetails?.court_name) parts.push(match.courtDetails.court_name);
  else if (match?.venue && match.venue !== 'TBD') parts.push(match.venue);
  if (match?.status === 'completed' && match?.score) parts.push(match.score);
  else if (match?.status && match.status !== 'upcoming') parts.push(match.status);
  return parts.filter(Boolean).join(' · ');
}

function renderInlineWidget(node, nodeId, teamContext, match) {
  if (!node?.widget) return null;
  return (
    <WidgetSection>
      <HelpInlineWidget
        widget={node.widget}
        nodeId={nodeId}
        node={node}
        teamContext={teamContext}
        match={match}
      />
    </WidgetSection>
  );
}

/** Full-page help wizard content (rendered at /player/help). */
export default function HelpWizardPanel() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMatch, setChatMatch] = useState(null);
  const [rescheduleAcknowledged, setRescheduleAcknowledged] = useState(false);
  const viewerPlayerId = user?.playerId || user?.id || null;
  const {
    nodeId,
    history,
    selectedTeam,
    selectedMatch,
    setSelectedMatch,
    rosterTeams,
    goToNode,
    goBack,
    startOver,
    pickTeamFromRoster,
  } = useHelpWizard();

  const node = getFlowNode(nodeId);

  const resolvedNodeId = useMemo(() => {
    if (!node) return FLOW_ROOT;
    if (node.type === 'team_picker' && selectedTeam?.teamId && node.next) {
      return node.next;
    }
    return nodeId;
  }, [node, nodeId, selectedTeam]);

  const resolvedNode = getFlowNode(resolvedNodeId) || getFlowNode(FLOW_ROOT);

  const needsMatches =
    resolvedNode?.type === 'match_picker' ||
    (resolvedNode?.type === 'inline' && resolvedNode.requiresMatch);
  const matchListOpenOnly = resolvedNode?.widget === 'opponent_contact';
  const { matches, loading: matchesLoading, error: matchesError } = useWizardMatches(
    needsMatches ? selectedTeam : null,
    { openOnly: matchListOpenOnly }
  );

  useEffect(() => {
    setRescheduleAcknowledged(false);
  }, [resolvedNodeId, selectedTeam?.teamId]);

  const handleTeamPick = useCallback(
    (teamRow) => {
      pickTeamFromRoster(teamRow);
      const n = getFlowNode(nodeId);
      if (n?.type === 'team_picker' && n.next) {
        goToNode(n.next, { pushHistory: true });
      }
    },
    [pickTeamFromRoster, nodeId, goToNode]
  );

  const handleCloseChat = useCallback(() => {
    setChatOpen(false);
    setChatMatch(null);
    syncPlayerNotificationsAfterMatchChat(queryClient);
  }, [queryClient]);

  const handleOpenMatchChat = useCallback((match) => {
    if (!match?.id) return;
    setChatMatch({
      id: match.id,
      opponent: match.opponent,
      opponentId: match.opponentId,
    });
    setChatOpen(true);
  }, []);

  const handleMatchPick = useCallback(
    (match) => {
      if (resolvedNode?.requireRescheduleAck && !rescheduleAcknowledged) return;
      if (resolvedNode?.openChatOnPick) {
        handleOpenMatchChat(match);
        return;
      }
      setSelectedMatch(match);
    },
    [
      resolvedNode?.requireRescheduleAck,
      resolvedNode?.openChatOnPick,
      rescheduleAcknowledged,
      handleOpenMatchChat,
      setSelectedMatch,
    ]
  );

  const renderRescheduleRulesGate = () => (
    <RescheduleInfoBanner>
      <RescheduleInfoTitle>{t('phase2.playerHelpWizard.rescheduleRules.title')}</RescheduleInfoTitle>
      <RescheduleInfoBody>{t('phase2.playerHelpWizard.rescheduleRules.lead')}</RescheduleInfoBody>
      <RescheduleInfoList>
        <li>{t('phase2.playerHelpWizard.rescheduleRules.pointNotice')}</li>
        <li>{t('phase2.playerHelpWizard.rescheduleRules.pointNoShow')}</li>
        <li>{t('phase2.playerHelpWizard.rescheduleRules.pointChat')}</li>
      </RescheduleInfoList>
      <RescheduleAckBtn type="button" onClick={() => setRescheduleAcknowledged(true)}>
        {t('phase2.playerHelpWizard.rescheduleRules.ackButton')}
      </RescheduleAckBtn>
    </RescheduleInfoBanner>
  );

  if (!resolvedNode) return null;

  const atRoot = nodeId === FLOW_ROOT && history.length === 0;
  const title = atRoot
    ? t('phase2.playerHelpWizard.panelTitle')
    : t(resolvedNode.titleKey || 'phase2.playerHelpWizard.panelTitle');
  const body = atRoot
    ? t('phase2.playerHelpWizard.nodes.root.body')
    : resolvedNode.bodyKey
      ? t(resolvedNode.bodyKey)
      : '';
  const hint = resolvedNode.hintKey ? t(resolvedNode.hintKey) : null;
  const stub = resolvedNode.stubKey ? t(resolvedNode.stubKey) : null;
  const canGoBack = history.length > 0;
  const TitleTag = atRoot ? PageTitle : StepTitle;
  const LeadTag = atRoot ? PageLead : StepLead;

  const renderContent = () => {
    if (node?.type === 'team_picker' && !selectedTeam) {
      const teams = Array.isArray(rosterTeams) ? rosterTeams.filter((x) => x?.teamId || x?.team_id) : [];
      if (teams.length === 0) {
        return (
          <>
            <Muted>{t('phase2.playerHelpWizard.noTeams')}</Muted>
            <TertiaryLink to="/player/teams">{t('phase2.playerHelpWizard.actions.openTeams')}</TertiaryLink>
          </>
        );
      }
      return (
        <>
          {teams.map((team) => {
            const name = team.teamName || team.team_name || `#${team.teamId || team.team_id}`;
            return (
              <ChoiceBtn key={String(team.teamId || team.team_id)} type="button" onClick={() => handleTeamPick(team)}>
                <ChoiceEmoji>👥</ChoiceEmoji>
                {name}
              </ChoiceBtn>
            );
          })}
        </>
      );
    }

    if (resolvedNode.type === 'menu') {
      return (resolvedNode.choices || []).map((c) => (
        <ChoiceBtn key={c.next} type="button" onClick={() => goToNode(c.next)}>
          {c.emoji ? <ChoiceEmoji>{c.emoji}</ChoiceEmoji> : null}
          {t(c.labelKey)}
        </ChoiceBtn>
      ));
    }

    if (resolvedNode.type === 'match_picker') {
      if (!selectedTeam) {
        return <Muted>{t('phase2.playerHelpWizard.pickTeamFirst')}</Muted>;
      }
      if (matchesLoading) {
        return <Muted>{t('phase2.playerHelpWizard.loadingMatches')}</Muted>;
      }
      if (matchesError) {
        return <Muted>{t('phase2.playerHelpWizard.matchesError')}</Muted>;
      }
      if (matches.length === 0) {
        return <Muted>{t('phase2.playerHelpWizard.noMatches')}</Muted>;
      }

      const needsRescheduleAck = resolvedNode.requireRescheduleAck && !rescheduleAcknowledged;

      return (
        <>
          {resolvedNode.requireRescheduleAck && needsRescheduleAck
            ? renderRescheduleRulesGate()
            : null}
          {resolvedNode.requireRescheduleAck && rescheduleAcknowledged ? (
            <RescheduleReminder>{t('phase2.playerHelpWizard.rescheduleRules.reminder')}</RescheduleReminder>
          ) : null}
          {!needsRescheduleAck
            ? matches.map((m) => {
            const opponentName =
              m.opponent || t('phase2.playerHelpWizard.matchFallback', { id: String(m.id).slice(0, 8) });
            const active = !resolvedNode.openChatOnPick && selectedMatch?.id === m.id;
            const meta = matchPickerMeta(m, t);
            const showContact = resolvedNode.widget === 'opponent_contact';
            return (
              <React.Fragment key={m.id}>
                <MatchPickCard
                  type="button"
                  data-active={active ? 'true' : 'false'}
                  onClick={() => handleMatchPick(m)}
                >
                  <MatchOpponentLogo match={m} />
                  <MatchPickBody>
                    <MatchOpponentLine>
                      <MatchVs>{t('teamDashboardPage.vsLabel', { defaultValue: 'vs' })}</MatchVs>
                      <span>{opponentName}</span>
                    </MatchOpponentLine>
                    {meta ? <MatchMeta>{meta}</MatchMeta> : null}
                  </MatchPickBody>
                  {resolvedNode.openChatOnPick ? (
                    <MatchChatHint aria-hidden>💬</MatchChatHint>
                  ) : showContact ? (
                    <MatchChatHint aria-hidden>📞</MatchChatHint>
                  ) : null}
                </MatchPickCard>
                {selectedMatch?.id === m.id && resolvedNode.widget
                  ? renderInlineWidget(resolvedNode, resolvedNodeId, selectedTeam, selectedMatch)
                  : null}
              </React.Fragment>
            );
            })
            : null}
        </>
      );
    }

    if (resolvedNode.type === 'inline') {
      if (resolvedNode.requiresTeam && !selectedTeam?.teamId) {
        return <Muted>{t('phase2.playerHelpWizard.pickTeamFirst')}</Muted>;
      }
      return (
        <>
          {stub ? <Stub>{stub}</Stub> : null}
          {renderInlineWidget(resolvedNode, resolvedNodeId, selectedTeam, selectedMatch)}
        </>
      );
    }

    return null;
  };

  return (
    <Page>
      {canGoBack ? (
        <TopToolbar>
          <SecondaryBtn type="button" onClick={goBack}>
            <FaArrowLeft style={{ marginRight: 6 }} aria-hidden />
            {t('phase2.playerHelpWizard.back')}
          </SecondaryBtn>
        </TopToolbar>
      ) : null}
      <TitleTag>{title}</TitleTag>
      {body ? <LeadTag>{body}</LeadTag> : null}
      {hint ? <Hint>{hint}</Hint> : null}
      {renderContent()}
      {!atRoot ? (
        <Toolbar>
          <SecondaryBtn type="button" onClick={startOver}>
            {t('phase2.playerHelpWizard.startOver')}
          </SecondaryBtn>
        </Toolbar>
      ) : null}
      {chatMatch && selectedTeam?.teamId ? (
        <MatchChatModal
          isOpen={chatOpen}
          onClose={handleCloseChat}
          matchId={chatMatch.id}
          opponentName={chatMatch.opponent}
          opponentTeamId={chatMatch.opponentId}
          teamId={selectedTeam.teamId}
          viewerPlayerId={viewerPlayerId}
        />
      ) : null}
    </Page>
  );
}
